import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ImageGallery from 'react-image-gallery';

import { RTL } from 'arbitrip-common/translation/const';

import roomPlaceholder from '@/img/hotel_page/room_placeholder.png';

import { IconChevron, IconPhotos } from './Rm3Icons';

const STEP_PREV = -1;
const STEP_NEXT = 1;

/**
 * Container photos (spec §1.2), reused by the room-details modal (spec P2.5) through the optional props.
 * `images` is the owner's working list (`useRm3Photos`): a photo that fails is reported via `onImageError` and dropped.
 */
function Rm3PhotoCarousel({
    images,
    alt,
    onOpen,
    startIndex = 0,
    onSlide,
    onImageError,
    galleryRef,
    className,
    showCount = true,
}) {
    const { t, i18n } = useTranslation();
    const ownGalleryRef = useRef(null);
    const gallery = galleryRef || ownGalleryRef;
    const isRtl = i18n.dir() === RTL;
    const count = images.length;
    const items = useMemo(() => images.map((url) => ({ original: url })), [images]);
    // Remount on a different photo list (also a photo dropped) at the owner's index: the gallery never re-reads it.
    const galleryKey = useMemo(() => images.join(' '), [images]);
    const imageClassName = `rm3-photo__image${onOpen ? ' rm3-photo__image--clickable' : ''}`;

    const renderItem = (item) => (
        <img
            className={imageClassName}
            src={item.original}
            alt={alt}
            onClick={onOpen || undefined}
            onError={onImageError ? () => onImageError(item.original) : undefined}
            data-testid="rm3-room-photo"
        />
    );

    // The gallery reports a slide only after its transition; the owner hears the target at once (a skipped click is
    // corrected by that later report).
    const reportSlide = (step) => {
        if (onSlide && gallery.current && count > 0) {
            onSlide((gallery.current.getCurrentIndex() + step + count) % count);
        }
    };

    const renderNav = (direction) =>
        function Rm3PhotoNav(slide, disabled) {
            return (
                <button
                    type="button"
                    className={`rm3-photo__nav rm3-photo__nav--${direction}`}
                    aria-label={t(direction === 'prev' ? 'HotelDealsRm3.PreviousImage' : 'HotelDealsRm3.NextImage')}
                    disabled={disabled}
                    onClick={(event) => {
                        event.stopPropagation();
                        reportSlide(direction === 'prev' ? STEP_PREV : STEP_NEXT);
                        slide();
                    }}
                    data-testid={`rm3-photo-${direction}`}
                >
                    <IconChevron className={`rm3-chevron${direction === 'prev' ? ' rm3-chevron--back' : ''}`} />
                </button>
            );
        };

    return (
        <div className={className ? `rm3-photo ${className}` : 'rm3-photo'}>
            {count > 0 ? (
                <ImageGallery
                    key={galleryKey}
                    ref={gallery}
                    items={items}
                    startIndex={Math.min(Math.max(startIndex, 0), count - 1)}
                    onSlide={onSlide}
                    renderItem={renderItem}
                    // The gallery's left/right callbacks are physical; with isRTL, "left" slides to the next image.
                    renderLeftNav={renderNav(isRtl ? 'next' : 'prev')}
                    renderRightNav={renderNav(isRtl ? 'prev' : 'next')}
                    isRTL={isRtl}
                    infinite
                    lazyLoad
                    disableArrowKeys
                    showThumbnails={false}
                    showPlayButton={false}
                    showFullscreenButton={false}
                />
            ) : (
                <img
                    className={imageClassName}
                    src={roomPlaceholder}
                    alt={alt}
                    onClick={onOpen || undefined}
                    data-testid="rm3-room-photo"
                />
            )}
            {showCount && count > 0 && (
                <div
                    className="rm3-photo__count"
                    aria-label={t('HotelDealsRm3.PhotoCount', { count })}
                    data-testid="rm3-photo-count"
                >
                    <IconPhotos />
                    <span dir="ltr">{count}</span>
                </div>
            )}
        </div>
    );
}

export default Rm3PhotoCarousel;
